import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';

export interface UploadTask {
  id: string;
  fileName: string;
  fileSize: number;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'failed';
  progress: number;
  startTime: Date;
  endTime?: Date;
  error?: string;
  siteId: string;
  userId: string;
  module: string;
  file?: File; // Store the actual file reference
}

export interface UploadNotification {
  type: 'info' | 'success' | 'warning' | 'error';
  title: string;
  message: string;
  taskId: string;
  timestamp: Date;
  actionRequired?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class BackgroundUploadService {
  private uploadTasksSubject = new BehaviorSubject<UploadTask[]>([]);
  private notificationsSubject = new Subject<UploadNotification>();
  private uploadQueue: UploadTask[] = [];
  private activeUploads = new Map<string, UploadTask>();
  private maxConcurrentUploads = 2;

  public uploadTasks$ = this.uploadTasksSubject.asObservable();
  public notifications$ = this.notificationsSubject.asObservable();

  constructor(private http: HttpClient) {
    // Initialize with any existing uploads from localStorage
    this.loadPersistedUploads();
  }

  /**
   * Add a new upload task to the queue
   */
  addUploadTask(file: File, siteId: string, userId: string, module: string): string {
    const taskId = this.generateTaskId();
    const task: UploadTask = {
      id: taskId,
      fileName: file.name,
      fileSize: file.size,
      status: 'pending',
      progress: 0,
      startTime: new Date(),
      siteId,
      userId,
      module,
      file: file // Store the file reference
    };

    this.uploadQueue.push(task);
    this.updateUploadTasks();
    this.addNotification({
      type: 'info',
      title: 'Upload Queued',
      message: `"${file.name}" has been added to the upload queue. You will be notified when processing begins.`,
      taskId,
      timestamp: new Date()
    });

    // Start processing if there's capacity
    this.processQueue();
    return taskId;
  }

  /**
   * Process the upload queue
   */
  private processQueue(): void {
    while (this.activeUploads.size < this.maxConcurrentUploads && this.uploadQueue.length > 0) {
      const task = this.uploadQueue.shift();
      if (task) {
        this.startUpload(task);
      }
    }
  }

  /**
   * Start uploading a specific task
   */
  private async startUpload(task: UploadTask): Promise<void> {
    this.activeUploads.set(task.id, task);
    task.status = 'uploading';
    this.updateUploadTasks();

    this.addNotification({
      type: 'info',
      title: 'Upload Started',
      message: `"${task.fileName}" is now being uploaded. You can continue using the system.`,
      taskId: task.id,
      timestamp: new Date()
    });

    try {
      // Simulate file upload with progress tracking
      await this.uploadFileWithProgress(task);
      
      // Move to processing status
      task.status = 'processing';
      task.progress = 100;
      this.updateUploadTasks();

      this.addNotification({
        type: 'info',
        title: 'Processing Started',
        message: `"${task.fileName}" upload completed. Data is now being processed on the server.`,
        taskId: task.id,
        timestamp: new Date()
      });

      // Simulate server processing
      await this.processFileOnServer(task);

      // Mark as completed
      task.status = 'completed';
      task.endTime = new Date();
      this.updateUploadTasks();

      this.addNotification({
        type: 'success',
        title: 'Upload Completed',
        message: `"${task.fileName}" has been successfully processed and is now available in your system.`,
        taskId: task.id,
        timestamp: new Date()
      });

    } catch (error) {
      task.status = 'failed';
      task.error = error.message;
      task.endTime = new Date();
      this.updateUploadTasks();

      this.addNotification({
        type: 'error',
        title: 'Upload Failed',
        message: `"${task.fileName}" failed to process. ${error.message}`,
        taskId: task.id,
        timestamp: new Date(),
        actionRequired: true
      });
    } finally {
      this.activeUploads.delete(task.id);
      this.persistUploads();
      this.processQueue(); // Process next item in queue
    }
  }

  /**
   * Upload file with progress tracking
   */
  private async uploadFileWithProgress(task: UploadTask): Promise<void> {
    return new Promise((resolve, reject) => {
      // Simulate progress updates
      const interval = setInterval(() => {
        if (task.progress < 90) {
          task.progress += Math.random() * 20;
          this.updateUploadTasks();
        } else {
          clearInterval(interval);
          task.progress = 90;
          this.updateUploadTasks();
          resolve();
        }
      }, 200);
    });
  }

  /**
   * Process file on server
   */
  private async processFileOnServer(task: UploadTask): Promise<void> {
    if (!task.file) {
      throw new Error('File reference not found');
    }

    // Create FormData exactly like the original implementation
    const formData: FormData = new FormData();
    formData.append('site_excel', task.file);
    formData.append('purpose', task.module === 'opex' ? 'upload_opexdata' : 'upload_data');
    formData.append('site_id', task.siteId);
    formData.append('user_id', task.userId);

    let response: any;
    
    // Use the correct endpoint based on module type
    if (task.module === 'opex') {
      // For opex, use the opex endpoint
      response = await this.http.post(`${environment.baseUri}opex/upload_opexdata`, formData).toPromise();
    } else {
      // For process, use the category endpoint
      response = await this.http.post(`${environment.nodeUrl1}category/upload_data`, formData).toPromise();
    }
    
    if (response && response.status !== 1) {
      throw new Error(response.msg || 'Server processing failed');
    }
  }

  /**
   * Get upload status for a specific task
   */
  getUploadStatus(taskId: string): UploadTask | undefined {
    return this.uploadTasksSubject.value.find(task => task.id === taskId);
  }

  /**
   * Cancel an upload task
   */
  cancelUpload(taskId: string): void {
    const task = this.uploadTasksSubject.value.find(t => t.id === taskId);
    if (task && (task.status === 'pending' || task.status === 'uploading')) {
      task.status = 'failed';
      task.error = 'Cancelled by user';
      task.endTime = new Date();
      this.updateUploadTasks();
      
      this.addNotification({
        type: 'warning',
        title: 'Upload Cancelled',
        message: `"${task.fileName}" upload has been cancelled.`,
        taskId: task.id,
        timestamp: new Date()
      });
    }
  }

  /**
   * Retry a failed upload
   */
  retryUpload(taskId: string): void {
    const task = this.uploadTasksSubject.value.find(t => t.id === taskId);
    if (task && task.status === 'failed') {
      task.status = 'pending';
      task.progress = 0;
      task.error = undefined;
      task.startTime = new Date();
      task.endTime = undefined;
      
      this.uploadQueue.push(task);
      this.updateUploadTasks();
      this.processQueue();
    }
  }

  /**
   * Clear completed uploads
   */
  clearCompletedUploads(): void {
    const tasks = this.uploadTasksSubject.value.filter(task => task.status !== 'completed');
    this.uploadTasksSubject.next(tasks);
    this.persistUploads();
  }

  /**
   * Clear all uploads (for stuck uploads)
   */
  clearAllUploads(): void {
    this.uploadQueue = [];
    this.activeUploads.clear();
    this.uploadTasksSubject.next([]);
    this.persistUploads();
  }

  /**
   * Force complete a stuck upload
   */
  forceCompleteUpload(taskId: string): void {
    const task = this.uploadTasksSubject.value.find(t => t.id === taskId);
    if (task) {
      task.status = 'completed';
      task.endTime = new Date();
      task.progress = 100;
      this.activeUploads.delete(taskId);
      this.updateUploadTasks();
      
      this.addNotification({
        type: 'success',
        title: 'Upload Force Completed',
        message: `"${task.fileName}" has been marked as completed.`,
        taskId: task.id,
        timestamp: new Date()
      });
    }
  }

  /**
   * Update upload tasks and persist to localStorage
   */
  private updateUploadTasks(): void {
    const allTasks = [...this.uploadQueue, ...Array.from(this.activeUploads.values())];
    this.uploadTasksSubject.next(allTasks);
    this.persistUploads();
  }

  /**
   * Add notification
   */
  private addNotification(notification: UploadNotification): void {
    this.notificationsSubject.next(notification);
  }

  /**
   * Generate unique task ID
   */
  private generateTaskId(): string {
    return `upload_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Persist uploads to localStorage
   */
  private persistUploads(): void {
    const tasks = this.uploadTasksSubject.value;
    localStorage.setItem('upload_tasks', JSON.stringify(tasks));
  }

  /**
   * Load persisted uploads from localStorage
   */
  private loadPersistedUploads(): void {
    const stored = localStorage.getItem('upload_tasks');
    if (stored) {
      try {
        const tasks: UploadTask[] = JSON.parse(stored);
        // Only restore pending/uploading tasks
        const activeTasks = tasks.filter(task => 
          task.status === 'pending' || task.status === 'uploading' || task.status === 'processing'
        );
        this.uploadTasksSubject.next(activeTasks);
      } catch (error) {
        console.error('Failed to load persisted uploads:', error);
      }
    }
  }
}
